import React, { useState } from 'react';
import { DriverFormData } from '../../types/driver';

interface UploadedFileUrls {
  licenseFront: string | null;
  licenseBack: string | null;
}

interface LicenseStepProps {
  data: DriverFormData;
  updateData: (data: Partial<DriverFormData>) => void;
  onFileUpload: (files: File[], type: keyof UploadedFileUrls) => Promise<void>;
  uploadedUrls: UploadedFileUrls;
}

const LicenseStep: React.FC<LicenseStepProps> = ({ 
  data, 
  updateData, 
  onFileUpload,
  uploadedUrls 
}) => {
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    updateData({ [name]: value });
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      
      if (name === 'licenseFront') {
        setUploadingFront(true);
      } else if (name === 'licenseBack') {
        setUploadingBack(true);
      }

      try {
        await onFileUpload([file], name as keyof UploadedFileUrls);
      } catch (error) {
        alert(`Failed to upload ${name === 'licenseFront' ? 'front' : 'back'} license photo`);
      } finally {
        if (name === 'licenseFront') {
          setUploadingFront(false);
        } else if (name === 'licenseBack') {
          setUploadingBack(false);
        }
      }
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Driver's License Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            License Number *
          </label>
          <input
            type="text"
            name="licenseNumber"
            value={data.licenseNumber}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            License Expiry Date *
          </label>
          <input
            type="date"
            name="licenseExpiry"
            value={data.licenseExpiry}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Years of Driving Experience *
          </label>
          <select
            name="drivingExperience"
            value={data.drivingExperience}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Select experience</option>
            <option value="0-1">0-1 years</option>
            <option value="1-3">1-3 years</option>
            <option value="3-5">3-5 years</option>
            <option value="5-10">5-10 years</option>
            <option value="10+">10+ years</option>
          </select>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            License Front Photo *
          </label>
          <input
            type="file"
            name="licenseFront"
            onChange={handleFileChange}
            accept="image/*"
            disabled={uploadingFront}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            required
          />
          <div className="flex items-center mt-2">
            {uploadingFront && (
              <div className="text-blue-600 text-sm">Uploading...</div>
            )}
            {uploadedUrls.licenseFront && !uploadingFront && (
              <div className="text-green-600 text-sm">✓ Uploaded successfully</div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Upload a clear photo of the front of your license</p>
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            License Back Photo *
          </label>
          <input
            type="file"
            name="licenseBack"
            onChange={handleFileChange}
            accept="image/*"
            disabled={uploadingBack}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            required
          />
          <div className="flex items-center mt-2">
            {uploadingBack && (
              <div className="text-blue-600 text-sm">Uploading...</div>
            )}
            {uploadedUrls.licenseBack && !uploadingBack && (
              <div className="text-green-600 text-sm">✓ Uploaded successfully</div>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Upload a clear photo of the back of your license</p>
        </div>
      </div>
    </div>
  );
};

export default LicenseStep;